import { DECISIONS } from './decisions.js'

/**
 * The kit's ONLY date / money formatting. The audit found ~71 date helpers and
 * 38 copies of the Thai month names; pages on the kit use these instead.
 *
 * Every date is read as Bangkok wall-clock, whatever the viewer's machine says.
 * Accepted inputs (the shapes Xano actually returns):
 *   - epoch milliseconds (number or digit string)      → Bangkok time
 *   - epoch seconds (< 1e11)                            → Bangkok time
 *   - 'YYYY-MM-DD' date-only string                     → that calendar day, no shift
 *   - any other ISO string / Date                       → Bangkok time
 * Empty, null, 0 and unparseable values render as '—', never as 1970.
 */

const BKK_OFFSET_MS = 7 * 3600e3

export const TH_MONTHS_SHORT = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']
export const TH_MONTHS_LONG = ['มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน', 'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม']
export const TH_DAYS_SHORT = ['อา.', 'จ.', 'อ.', 'พ.', 'พฤ.', 'ศ.', 'ส.']

const pad = (n) => String(n).padStart(2, '0')

function fromMs(ms) {
  if (!Number.isFinite(ms)) return null
  const x = new Date(ms + BKK_OFFSET_MS)
  return { y: x.getUTCFullYear(), m: x.getUTCMonth() + 1, d: x.getUTCDate(), hh: x.getUTCHours(), mi: x.getUTCMinutes(), dow: x.getUTCDay() }
}

/** Any accepted input → { y, m, d, hh, mi, dow } in Bangkok, or null. hh/mi are null for a date-only value. */
export function dateParts(v) {
  if (v == null || v === '' || v === 0) return null
  if (v instanceof Date) return fromMs(v.getTime())
  if (typeof v === 'number') return fromMs(v < 1e11 ? v * 1000 : v)
  const s = String(v).trim()
  if (/^\d+$/.test(s)) {
    const n = Number(s)
    if (!n) return null
    return fromMs(n < 1e11 ? n * 1000 : n)
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s)
  if (m) {
    const y = Number(m[1]); const mo = Number(m[2]); const d = Number(m[3])
    if (mo < 1 || mo > 12 || d < 1 || d > 31) return null
    return { y, m: mo, d, hh: null, mi: null, dow: new Date(Date.UTC(y, mo - 1, d)).getUTCDay() }
  }
  const t = Date.parse(s)
  return Number.isFinite(t) ? fromMs(t) : null
}

const year = (y, era) => (era === 'BE' ? y + 543 : y)

/** 'dd/mm/yyyy' — the default date everywhere on screen. */
export function fmtDate(v, { era = DECISIONS.screenEra } = {}) {
  const p = dateParts(v)
  return p ? `${pad(p.d)}/${pad(p.m)}/${year(p.y, era)}` : '—'
}

/** 'dd/mm/yyyy HH:mm' — audit lines, "บันทึกเมื่อ". Falls back to fmtDate for date-only values. */
export function fmtDateTime(v, opts = {}) {
  const p = dateParts(v)
  if (!p) return '—'
  if (p.hh == null) return fmtDate(v, opts)
  return `${fmtDate(v, opts)} ${pad(p.hh)}:${pad(p.mi)}`
}

/** '8 ส.ค. 2026' — headings and cards where a readable date helps. `weekday` → 'จ. 8 ส.ค. 2026'. */
export function fmtDateText(v, { era = DECISIONS.screenEra, weekday = false } = {}) {
  const p = dateParts(v)
  if (!p) return '—'
  const s = `${p.d} ${TH_MONTHS_SHORT[p.m - 1]} ${year(p.y, era)}`
  return weekday ? `${TH_DAYS_SHORT[p.dow]} ${s}` : s
}

/** 'ส.ค. 2026' (or 'สิงหาคม 2026' with long). Month pickers, month group headings. */
export function fmtMonth(v, { era = DECISIONS.screenEra, long = false } = {}) {
  const p = dateParts(v)
  if (!p) return '—'
  return `${(long ? TH_MONTHS_LONG : TH_MONTHS_SHORT)[p.m - 1]} ${year(p.y, era)}`
}

/** 'HH:mm' in Bangkok; '' for a date-only value. */
export function fmtTime(v) {
  const p = dateParts(v)
  return p && p.hh != null ? `${pad(p.hh)}:${pad(p.mi)}` : ''
}

/** '8 สิงหาคม 2569' — PRINTED / legal documents only. Always พ.ศ. */
export function fmtDocDate(v) {
  const p = dateParts(v)
  return p ? `${p.d} ${TH_MONTHS_LONG[p.m - 1]} ${p.y + 543}` : '—'
}

/**
 * The small log line in a printed document's FOOTER — the one exception to
 * "documents are พ.ศ." (decision #1). ค.ศ., English, compact: 'Printed 18/10/2026 10:30'.
 */
export function fmtDocFooterStamp(v, { label = 'Printed' } = {}) {
  const s = fmtDateTime(v, { era: 'CE' })
  return s === '—' ? '' : `${label} ${s}`
}

/** Join footer facts: docFooterLine({ printedAt, treatmentIds: '1234' }) → 'Treatment ID 1234 · Printed 18/10/2026 10:30'. */
export function docFooterLine({ printedAt, treatmentIds, extra = [] } = {}) {
  return [
    treatmentIds ? `Treatment ID ${treatmentIds}` : '',
    ...extra,
    printedAt != null ? fmtDocFooterStamp(printedAt) : '',
  ].filter(Boolean).join(' · ')
}

/** Any accepted input → 'YYYY-MM-DD' (Bangkok), or ''. For <input type="date"> values and API params. */
export function toISODate(v) {
  const p = dateParts(v)
  return p ? `${p.y}-${pad(p.m)}-${pad(p.d)}` : ''
}

/** The clinic's today, 'YYYY-MM-DD' in Bangkok — never the browser's clock. */
export function bkkTodayISO(now = Date.now()) {
  return toISODate(now)
}

/**
 * Money. Default: whole baht, en-US grouping, no currency sign — '12,345'.
 *   decimals  number of decimals (0 default; 2 for accounting screens)
 *   maxDecimals  show UP TO this many, only when the value has them: 427.5 → '427.5',
 *            427 → '427' (DF and fee boxes — never round 427.5 to 428, never pad 427.50)
 *   currency  'none' | 'prefix' ('฿12,345') | 'suffix' ('12,345 บาท')
 *   zero      what a zero shows: '0' (default) or e.g. '—'
 * null / '' / non-numeric → '—' (unknown is never shown as 0).
 */
export function fmtMoney(n, { decimals = 0, maxDecimals, currency = 'none', zero = null } = {}) {
  if (n == null || n === '' || !Number.isFinite(Number(n))) return '—'
  const v = Number(n)
  if (v === 0 && zero != null) return zero
  const min = maxDecimals != null ? 0 : decimals
  const max = maxDecimals != null ? maxDecimals : decimals
  const body = Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: min, maximumFractionDigits: max })
  const sign = v < 0 ? '-' : ''
  if (currency === 'prefix') return `${sign}฿${body}`
  if (currency === 'suffix') return `${sign}${body} บาท`
  return `${sign}${body}`
}

/** Plain count / quantity with grouping. */
export function fmtNumber(n, { decimals = 0 } = {}) {
  if (n == null || n === '' || !Number.isFinite(Number(n))) return '—'
  return Number(n).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
}

/** File size, '1.4 MB'. */
export function fmtBytes(n) {
  let v = Number(n)
  if (!Number.isFinite(v) || v <= 0) return '—'
  const units = ['B', 'KB', 'MB', 'GB']
  let i = 0
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++ }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`
}

/** Thai dictionary order, numbers as numbers ("2" before "10"). Shared by every sortable table. */
export const THAI_COLLATOR = new Intl.Collator('th', { numeric: true, sensitivity: 'base' })
